import {
  CATCH_ANDROID_PICKER_UNREADABLE_ERROR_CODE,
  CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE,
  CATCH_MULTIPART_BUILD_ERROR_CODE,
  CATCH_MULTIPART_BUILD_ERROR_MESSAGE,
  buildCatchMediaClientError,
  buildRejectedImageSelectionState,
  buildUploadFilename,
  getFilenameExtension,
  inferSelectedMediaType,
  normalizeBlobMimeType,
  normalizeMimeType,
} from "./catchMediaConstants";
import { computeSubmitReady } from "./catchSubmitUtils";

export const getCurrentUserAgent = () =>
  typeof navigator !== "undefined" && typeof navigator.userAgent === "string"
    ? navigator.userAgent
    : "";

// UA check for Android Chrome family. We use UA string (not UA Client Hints)
// because (a) iOS Safari/Firefox desktop don't expose UA-CH consistently, and
// (b) this gates a *mobile-only* code path — false negatives just fall back to
// the existing multi-strategy pipeline, which is safe. Callers may also pass
// `navigator.userAgentData?.platform === "Android"` as the UA arg.
export const isAndroidChromeUserAgent = (userAgent) => {
  const normalizedUserAgent =
    typeof userAgent === "string" ? userAgent : "";

  return (
    /android/i.test(normalizedUserAgent) &&
    /chrome\/\d+/i.test(normalizedUserAgent) &&
    !/edga|edgios|opr|opera|samsungbrowser|firefox|fxios/i.test(
      normalizedUserAgent
    )
  );
};

export const isAndroidPlatform = () => {
  if (typeof navigator === "undefined") return false;
  const uaData = navigator.userAgentData;
  if (uaData && typeof uaData.platform === "string") {
    if (uaData.platform.toLowerCase() === "android") return true;
  }
  return isAndroidChromeUserAgent(
    typeof navigator.userAgent === "string" ? navigator.userAgent : ""
  );
};

const hasSnapshotErrorName = (attempt, expectedName) => {
  const actualName =
    typeof attempt?.errorName === "string" ? attempt.errorName : "";
  const actualMessage =
    typeof attempt?.errorMessage === "string" ? attempt.errorMessage : "";
  const normalizedExpected =
    typeof expectedName === "string" ? expectedName.toLowerCase() : "";

  return (
    actualName.toLowerCase() === normalizedExpected ||
    actualMessage.toLowerCase().includes(normalizedExpected)
  );
};

const hasSnapshotFailedToFetch = (attempt) => {
  const actualMessage =
    typeof attempt?.errorMessage === "string" ? attempt.errorMessage : "";
  return actualMessage.toLowerCase().includes("failed to fetch");
};

const SNAPSHOT_RETRY_DELAYS_MS = [80, 220];

const waitForSnapshotRetry = (delayMs) =>
  new Promise((resolve) => {
    setTimeout(resolve, Math.max(0, Number.isFinite(delayMs) ? delayMs : 0));
  });

export const classifySnapshotReadFailure = (attemptFailures, context = {}) => {
  const failures = Array.isArray(attemptFailures) ? attemptFailures : [];
  const arrayBufferFailure = failures.find(
    (failure) => failure?.strategy === "arrayBuffer"
  );
  const responseFailure = failures.find(
    (failure) => failure?.strategy === "response"
  );
  const sliceFailure = failures.find((failure) => failure?.strategy === "slice");
  const userAgent =
    typeof context.userAgent === "string" && context.userAgent
      ? context.userAgent
      : getCurrentUserAgent();
  const requestedType =
    typeof context.requestedType === "string" ? context.requestedType : "image";
  const isAndroidChrome = isAndroidChromeUserAgent(userAgent);
  const isUnreadableAcrossStrategies =
    hasSnapshotErrorName(arrayBufferFailure, "NotReadableError") &&
    hasSnapshotFailedToFetch(responseFailure) &&
    hasSnapshotErrorName(sliceFailure, "NotReadableError");

  if (isAndroidChrome && requestedType === "image" && isUnreadableAcrossStrategies) {
    return {
      failureCode: CATCH_ANDROID_PICKER_UNREADABLE_ERROR_CODE,
      failureMessage: CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE,
      failureReason: "android_chrome_picker_handle_unreadable",
      shouldSuggestCamera: false,
    };
  }

  return {
    failureCode: "",
    failureMessage: "",
    failureReason: "",
    shouldSuggestCamera: false,
  };
};

export const describeSnapshotFileObject = (file) => ({
  fileInstanceOfFile:
    typeof File === "function" && file ? file instanceof File : null,
  fileConstructorName:
    typeof file?.constructor?.name === "string" ? file.constructor.name : "",
  hasArrayBuffer: typeof file?.arrayBuffer === "function",
  hasFileReader:
    typeof FileReader === "function" &&
    typeof file !== "undefined" &&
    file !== null,
  hasSlice: typeof file?.slice === "function",
  hasStream: typeof file?.stream === "function",
  fileTag: Object.prototype.toString.call(file),
});

// Magic-byte sniffer for common image formats. Android Chrome's Photo Picker
// sometimes hands over File objects with empty `type` or
// `application/octet-stream`, so we inspect the first ~12 bytes of the already-
// read buffer before trusting the File's declared MIME.
//
// Decision table (in order):
//   1. buffer starts with FF D8 FF                              → image/jpeg
//   2. buffer starts with 89 50 4E 47 0D 0A 1A 0A               → image/png
//   3. buffer starts with "RIFF" and bytes 8..11 are "WEBP"     → image/webp
//   4. bytes 4..7 are "ftyp" and brand (8..11) matches heic|heix|hevc|hevx|mif1|msf1|heim|heis|hevm|hevs → image/heic
//   5. bytes 4..7 are "ftyp" and brand matches avif|avis        → image/avif
//   6. otherwise, if file.type starts with "image/"             → file.type (lowercased)
//   7. otherwise, if filename ext is .jpg/.jpeg                 → image/jpeg (conservative fallback)
//   8. otherwise                                                → "" (unknown; caller decides)
export const resolveImageMimeTypeFromBuffer = (file, buffer) => {
  const declared = normalizeMimeType(file?.type);
  const name = typeof file?.name === "string" ? file.name : "";
  const bytes =
    buffer && typeof buffer === "object" && buffer.byteLength > 0
      ? new Uint8Array(buffer, 0, Math.min(buffer.byteLength, 16))
      : null;

  if (bytes && bytes.length >= 3) {
    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
      return "image/jpeg";
    }
  }
  if (bytes && bytes.length >= 8) {
    if (
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47 &&
      bytes[4] === 0x0d &&
      bytes[5] === 0x0a &&
      bytes[6] === 0x1a &&
      bytes[7] === 0x0a
    ) {
      return "image/png";
    }
  }
  if (bytes && bytes.length >= 12) {
    // RIFF....WEBP
    if (
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
    ) {
      return "image/webp";
    }
    // ISO BMFF: bytes 4..7 == "ftyp"
    if (
      bytes[4] === 0x66 &&
      bytes[5] === 0x74 &&
      bytes[6] === 0x79 &&
      bytes[7] === 0x70
    ) {
      const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
      const heifBrands = new Set([
        "heic",
        "heix",
        "hevc",
        "hevx",
        "mif1",
        "msf1",
        "heim",
        "heis",
        "hevm",
        "hevs",
      ]);
      if (heifBrands.has(brand)) {
        return "image/heic";
      }
      if (brand === "avif" || brand === "avis") {
        return "image/avif";
      }
    }
  }

  if (declared && declared.startsWith("image/")) {
    return declared;
  }

  const ext = getFilenameExtension(name);
  if (ext === "jpg" || ext === "jpeg" || ext === "jpe" || ext === "jfif") {
    return "image/jpeg";
  }
  if (ext === "png") return "image/png";
  if (ext === "webp") return "image/webp";
  if (ext === "heic" || ext === "heif") return "image/heic";

  return "";
};

// Build a memory-backed File from an already-captured ArrayBuffer. Used after
// the eager read in useCatchMediaSelectionFlow kicks off on the native change
// event. Keeps filename + lastModified from the original picker File and
// stamps the resolved MIME type.
export const buildStabilizedFileFromBuffer = (file, buffer, resolvedMime) => {
  if (!buffer || typeof File !== "function") {
    return null;
  }
  const name = typeof file?.name === "string" ? file.name : "";
  const mimeType =
    (typeof resolvedMime === "string" && resolvedMime) ||
    (typeof file?.type === "string" && file.type) ||
    "application/octet-stream";
  const lastModified = Number.isFinite(file?.lastModified)
    ? file.lastModified
    : Date.now();
  try {
    return new File([buffer], name, { type: mimeType, lastModified });
  } catch {
    return null;
  }
};

export const HEIC_MIME_TYPES = new Set(["image/heic", "image/heif"]);

const readFileWithFileReader = (file) =>
  new Promise((resolve, reject) => {
    if (typeof FileReader !== "function") {
      reject(new Error("FileReader not available"));
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;
      if (result instanceof ArrayBuffer) {
        resolve(result);
        return;
      }

      if (ArrayBuffer.isView(result)) {
        resolve(result.buffer.slice(result.byteOffset, result.byteOffset + result.byteLength));
        return;
      }

      reject(new Error("FileReader did not return an ArrayBuffer"));
    };

    reader.onerror = () => {
      reject(reader.error || new Error("FileReader read failed"));
    };

    reader.onabort = () => {
      const abortError = new Error("FileReader read aborted");
      abortError.name = "AbortError";
      reject(abortError);
    };

    reader.readAsArrayBuffer(file);
  });

const getCurrentSelectionId = (resolver) =>
  typeof resolver === "function" ? resolver() : null;

const buildSnapshotDebugState = (
  file,
  context = {},
  currentSelectionIdResolver
) => {
  const inputEl = context.inputEl || null;
  const currentSize = Number.isFinite(file?.size) ? file.size : null;
  const currentType = typeof file?.type === "string" ? file.type : "";
  const currentLastModified = Number.isFinite(file?.lastModified)
    ? file.lastModified
    : null;
  const inputFilesLength = Number.isFinite(inputEl?.files?.length)
    ? inputEl.files.length
    : null;
  const inputFile = inputEl?.files?.[0] || null;
  const currentSelectionId = getCurrentSelectionId(currentSelectionIdResolver);

  return {
    name: typeof file?.name === "string" ? file.name : "",
    originalSize: Number.isFinite(context.originalSize)
      ? context.originalSize
      : currentSize,
    currentSize,
    sizeChangedBeforeFailure:
      Number.isFinite(context.originalSize) && currentSize !== context.originalSize,
    originalType:
      typeof context.originalType === "string" ? context.originalType : currentType,
    currentType,
    typeChangedBeforeFailure:
      typeof context.originalType === "string" && currentType !== context.originalType,
    lastModified: currentLastModified,
    originalLastModified: Number.isFinite(context.originalLastModified)
      ? context.originalLastModified
      : currentLastModified,
    lastModifiedChangedBeforeFailure:
      Number.isFinite(context.originalLastModified) &&
      currentLastModified !== context.originalLastModified,
    selectionId: context.selectionId ?? null,
    currentSelectionId,
    selectionIdChanged:
      context.selectionId != null && currentSelectionId !== context.selectionId,
    inputId: context.inputId || "",
    inputStillExists: Boolean(inputEl),
    inputIsConnected:
      typeof inputEl?.isConnected === "boolean" ? inputEl.isConnected : null,
    inputFilesLength,
    inputFilesStillSingle: inputFilesLength === 1,
    inputFileMatchesReference: Boolean(inputFile) ? inputFile === file : false,
    sameFileObjectReference: file === context.receivedFile,
    fileNameInInput:
      typeof inputFile?.name === "string" ? inputFile.name : "",
    fileSizeInInput: Number.isFinite(inputFile?.size) ? inputFile.size : null,
    fileTypeInInput: typeof inputFile?.type === "string" ? inputFile.type : "",
    fromChangeHandler: Boolean(context.fromChangeHandler),
    snapshotReadStartedImmediately: Boolean(context.snapshotReadStartedImmediately),
    fileListCountAtReceipt: Number.isFinite(context.fileListCountAtReceipt)
      ? context.fileListCountAtReceipt
      : null,
    ...describeSnapshotFileObject(file),
  };
};

export const readFileIntoArrayBufferWithFallbacks = async (
  file,
  context = {},
  currentSelectionIdResolver
) => {
  const strategies = [
    {
      name: "arrayBuffer",
      read: () => {
        if (typeof file?.arrayBuffer !== "function") {
          throw new Error("File.arrayBuffer not available");
        }
        return file.arrayBuffer();
      },
    },
    {
      name: "fileReader",
      read: () => readFileWithFileReader(file),
    },
    {
      name: "response",
      read: () => {
        if (typeof Response !== "function") {
          throw new Error("Response not available");
        }
        return new Response(file).arrayBuffer();
      },
    },
    {
      name: "slice",
      read: () => {
        if (typeof file?.slice !== "function") {
          throw new Error("File.slice not available");
        }
        const slicedBlob = file.slice(0, Number.isFinite(file?.size) ? file.size : 0);
        if (!slicedBlob || typeof slicedBlob.arrayBuffer !== "function") {
          throw new Error("Sliced blob does not support arrayBuffer");
        }
        return slicedBlob.arrayBuffer();
      },
    },
  ];
  const attemptedStrategies = [];
  const attemptFailures = [];
  let lastError = null;

  for (const strategy of strategies) {
    attemptedStrategies.push(strategy.name);
    const startMs =
      typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    console.log("[CATCH_IMG_DEBUG]", {
      stage: "snapshot_read_attempt",
      strategy: strategy.name,
      ...buildSnapshotDebugState(file, context, currentSelectionIdResolver),
    });

    try {
      const buffer = await strategy.read();
      const elapsedMs =
        (typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now()) - startMs;
      console.log("[CATCH_IMG_DEBUG]", {
        stage: "snapshot_read_success",
        strategy: strategy.name,
        elapsedMs: Number.isFinite(elapsedMs) ? Math.round(elapsedMs) : null,
        byteLength: Number.isFinite(buffer?.byteLength) ? buffer.byteLength : null,
        ...buildSnapshotDebugState(file, context, currentSelectionIdResolver),
      });
      return {
        buffer,
        strategy: strategy.name,
        attemptedStrategies,
      };
    } catch (error) {
      lastError = error;
      attemptFailures.push({
        strategy: strategy.name,
        errorName: error?.name,
        errorMessage: error?.message,
        errorStack: error?.stack,
      });
      const elapsedMs =
        (typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now()) - startMs;
      console.warn("[CATCH_IMG_DEBUG]", {
        stage: "snapshot_read_failed",
        strategy: strategy.name,
        elapsedMs: Number.isFinite(elapsedMs) ? Math.round(elapsedMs) : null,
        errorName: error?.name,
        errorMessage: error?.message,
        errorStack: error?.stack,
        ...buildSnapshotDebugState(file, context, currentSelectionIdResolver),
      });

      // Android Chrome Photo Picker short-circuit: if the first strategy
      // (arrayBuffer) fails with NotReadableError on an Android image handle,
      // the underlying content:// URI is already revoked. Running the three
      // fallback strategies on the same handle is guaranteed to fail too —
      // skip them, classify as the Android picker failure, and let the caller
      // surface the repick UX immediately. Desktop/iOS keep the full cascade.
      if (
        strategy.name === "arrayBuffer" &&
        error?.name === "NotReadableError" &&
        (typeof context?.requestedType !== "string" ||
          context.requestedType === "image") &&
        isAndroidChromeUserAgent(
          typeof context?.userAgent === "string"
            ? context.userAgent
            : getCurrentUserAgent()
        )
      ) {
        console.warn("[CATCH_IMG_DEBUG]", {
          stage: "snapshot_android_shortcircuit",
          strategy: strategy.name,
          errorName: error?.name,
          errorMessage: error?.message,
          skippedStrategies: strategies
            .slice(strategies.indexOf(strategy) + 1)
            .map((s) => s.name),
          selectionId: context?.selectionId ?? null,
          inputId: context?.inputId || "",
        });
        const shortCircuitError = new Error(
          error?.message || "Android picker handle unreadable"
        );
        shortCircuitError.name = error?.name || "NotReadableError";
        if (error?.stack) {
          shortCircuitError.stack = error.stack;
        }
        shortCircuitError.cause = error;
        shortCircuitError.attemptedStrategies = [...attemptedStrategies];
        shortCircuitError.attemptFailures = [...attemptFailures];
        shortCircuitError.failureCode = CATCH_ANDROID_PICKER_UNREADABLE_ERROR_CODE;
        shortCircuitError.failureMessage = CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE;
        shortCircuitError.failureReason = "android_chrome_picker_handle_unreadable";
        shortCircuitError.shouldSuggestCamera = false;
        shortCircuitError.shortCircuited = true;
        throw shortCircuitError;
      }
    }
  }

  const finalError = new Error(
    lastError?.message || "Snapshot read unavailable"
  );
  finalError.name = lastError?.name || "SnapshotReadFailed";
  if (lastError?.stack) {
    finalError.stack = lastError.stack;
  }
  if (lastError) {
    finalError.cause = lastError;
  }
  finalError.attemptedStrategies = attemptedStrategies;
  finalError.attemptFailures = attemptFailures;
  const classifiedFailure = classifySnapshotReadFailure(attemptFailures, context);
  finalError.failureCode = classifiedFailure.failureCode;
  finalError.failureMessage = classifiedFailure.failureMessage;
  finalError.failureReason = classifiedFailure.failureReason;
  finalError.shouldSuggestCamera = classifiedFailure.shouldSuggestCamera;
  throw finalError;
};

export const snapshotSelectedFile = async (
  file,
  context = {},
  currentSelectionIdResolver
) => {
  const name = typeof file?.name === "string" ? file.name : "";
  const originalType = typeof file?.type === "string" ? file.type : "";
  const resolvedType = originalType || "application/octet-stream";
  const originalSize = Number.isFinite(file?.size) ? file.size : null;
  const originalLastModified = Number.isFinite(file?.lastModified)
    ? file.lastModified
    : null;
  const resolvedLastModified = Date.now();
  const snapshotContext = {
    ...context,
    originalSize,
    originalType,
    originalLastModified,
    receivedFile: context.receivedFile || file,
    requestedType:
      typeof context.requestedType === "string" ? context.requestedType : "image",
    userAgent:
      typeof context.userAgent === "string" ? context.userAgent : getCurrentUserAgent(),
  };

  const baseLog = {
    name,
    type: resolvedType,
    originalSize,
    snapshotSize: null,
  };

  console.log("[CATCH_IMG_DEBUG]", {
    stage: "snapshot_start",
    ...baseLog,
    downstreamUsesStableSelectedFile: false,
    selectionId: context.selectionId ?? null,
    inputId: context.inputId || "",
    inputValueBeforeClear: context.inputValueBeforeClear || "",
    ...buildSnapshotDebugState(file, snapshotContext, currentSelectionIdResolver),
  });

  if (!file) {
    console.warn("[CATCH_IMG_DEBUG]", {
      stage: "snapshot_failed",
      ...baseLog,
      downstreamUsesStableSelectedFile: false,
      errorName: "Unsupported",
      errorMessage: "Selected file missing",
      selectionId: context.selectionId ?? null,
      inputId: context.inputId || "",
      ...buildSnapshotDebugState(file, snapshotContext, currentSelectionIdResolver),
    });
    return {
      status: "failed",
      snapshotFile: null,
    };
  }

  try {
    console.log("[CATCH_IMG_DEBUG]", {
      stage: "snapshot_before_arrayBuffer",
      selectionId: context.selectionId ?? null,
      inputId: context.inputId || "",
      name,
      type: resolvedType,
      originalSize,
    });
    const retryDelaysMs = SNAPSHOT_RETRY_DELAYS_MS;
    const maxRetryCount = retryDelaysMs.length;
    let readResult = null;
    let readStrategy = "";
    let attemptedStrategies = [];

    for (let retryCount = 0; retryCount <= maxRetryCount; retryCount += 1) {
      if (retryCount > 0) {
        console.log("[CATCH_IMG_DEBUG]", {
          stage: "snapshot_retry_attempt",
          selectionId: context.selectionId ?? null,
          inputId: context.inputId || "",
          retryCount,
          maxRetryCount,
          name,
          type: resolvedType,
          originalSize,
        });
      }

      try {
        readResult = await readFileIntoArrayBufferWithFallbacks(
          file,
          snapshotContext,
          currentSelectionIdResolver
        );
        readStrategy = readResult?.strategy || "";
        attemptedStrategies = Array.isArray(readResult?.attemptedStrategies)
          ? readResult.attemptedStrategies
          : [];

        if (retryCount > 0) {
          console.log("[CATCH_IMG_DEBUG]", {
            stage: "snapshot_retry_success",
            selectionId: context.selectionId ?? null,
            inputId: context.inputId || "",
            retryCount,
            maxRetryCount,
            readStrategy,
            attemptedStrategies,
          });
        }
        break;
      } catch (error) {
        const currentSelectionId = getCurrentSelectionId(currentSelectionIdResolver);
        const selectionIdChanged =
          context.selectionId != null && currentSelectionId !== context.selectionId;
        // Do not retry the short-circuited Android path: the picker handle is
        // gone, so every subsequent attempt would just repeat the same failure
        // on the same (already-revoked) File object.
        const shouldRetry =
          retryCount < maxRetryCount &&
          error?.failureCode === CATCH_ANDROID_PICKER_UNREADABLE_ERROR_CODE &&
          !selectionIdChanged &&
          !error?.shortCircuited;

        if (!shouldRetry) {
          if (
            error?.failureCode === CATCH_ANDROID_PICKER_UNREADABLE_ERROR_CODE &&
            retryCount > 0
          ) {
            console.warn("[CATCH_IMG_DEBUG]", {
              stage: "snapshot_retry_exhausted",
              selectionId: context.selectionId ?? null,
              inputId: context.inputId || "",
              retryCount,
              maxRetryCount,
              attemptedStrategies: Array.isArray(error?.attemptedStrategies)
                ? error.attemptedStrategies
                : [],
              selectionIdChanged,
            });
          }
          throw error;
        }

        const delayMs = retryDelaysMs[retryCount] ?? 0;
        console.warn("[CATCH_IMG_DEBUG]", {
          stage: "snapshot_retry_scheduled",
          selectionId: context.selectionId ?? null,
          inputId: context.inputId || "",
          retryCount: retryCount + 1,
          maxRetryCount,
          delayMs,
          attemptedStrategies: Array.isArray(error?.attemptedStrategies)
            ? error.attemptedStrategies
            : [],
          failureCode: error?.failureCode || "",
          failureReason: error?.failureReason || "",
        });
        await waitForSnapshotRetry(delayMs);
      }
    }

    const { buffer } = readResult || {};
    const currentSelectionId = getCurrentSelectionId(currentSelectionIdResolver);
    const selectionIdChanged =
      context.selectionId != null && currentSelectionId !== context.selectionId;
    console.log("[CATCH_IMG_DEBUG]", {
      stage: "snapshot_after_arrayBuffer",
      selectionId: context.selectionId ?? null,
      inputId: context.inputId || "",
      readStrategy,
      attemptedStrategies,
      selectionIdChanged,
    });
    if (selectionIdChanged) {
      console.warn("[CATCH_IMG_DEBUG]", {
        stage: "invariant_selection_changed_during_snapshot",
        selectionId: context.selectionId ?? null,
        currentSelectionId,
      });
    }
    const buildSnapshot = (inputBuffer) => {
      let nextFile = null;
      if (typeof File === "function") {
        try {
          nextFile = new File([inputBuffer], name, {
            type: resolvedType,
            lastModified: resolvedLastModified,
          });
        } catch {
          nextFile = null;
        }
      }

      if (!nextFile && typeof Blob === "function") {
        nextFile = new Blob([inputBuffer], { type: resolvedType });
        try {
          nextFile.name = name;
        } catch {
          // ignore
        }
        try {
          nextFile.lastModified = resolvedLastModified;
        } catch {
          // ignore
        }
      }

      return nextFile;
    };

    const snapshotFile = buildSnapshot(buffer);
    if (!snapshotFile) {
      throw new Error("Snapshot unavailable");
    }

    const snapshotSize = Number.isFinite(snapshotFile?.size)
      ? snapshotFile.size
      : null;

    console.log("[CATCH_IMG_DEBUG]", {
      stage: "snapshot_success",
      name,
      type: resolvedType,
      originalSize,
      snapshotSize,
      readStrategy,
      downstreamUsesStableSelectedFile: true,
    });

    return {
      status: "success",
      snapshotFile,
      readStrategy,
    };
  } catch (error) {
    console.warn("[CATCH_IMG_DEBUG]", {
      stage: "snapshot_failed",
      name,
      type: resolvedType,
      originalSize,
      snapshotSize: null,
      downstreamUsesStableSelectedFile: false,
      errorName: error?.name,
      errorMessage: error?.message,
      errorStack: error?.stack,
      attemptedStrategies: Array.isArray(error?.attemptedStrategies)
        ? error.attemptedStrategies
        : [],
      failureCode: typeof error?.failureCode === "string" ? error.failureCode : "",
      failureReason:
        typeof error?.failureReason === "string" ? error.failureReason : "",
      shouldSuggestCamera: Boolean(error?.shouldSuggestCamera),
      selectionId: context.selectionId ?? null,
      inputId: context.inputId || "",
      ...buildSnapshotDebugState(file, snapshotContext, currentSelectionIdResolver),
    });
    if (error?.failureCode === CATCH_ANDROID_PICKER_UNREADABLE_ERROR_CODE) {
      console.warn("[CATCH_IMG_DEBUG]", {
        stage: "snapshot_failed_android_picker_handle",
        selectionId: context.selectionId ?? null,
        inputId: context.inputId || "",
        failureCode: error.failureCode,
        failureReason: error.failureReason,
        attemptedStrategies: Array.isArray(error?.attemptedStrategies)
          ? error.attemptedStrategies
          : [],
        shouldSuggestCamera: Boolean(error?.shouldSuggestCamera),
      });
    }
    return {
      status: "failed",
      snapshotFile: null,
      failureCode:
        typeof error?.failureCode === "string" ? error.failureCode : "",
      failureMessage:
        typeof error?.failureMessage === "string" ? error.failureMessage : "",
      failureReason:
        typeof error?.failureReason === "string" ? error.failureReason : "",
      shouldSuggestCamera: Boolean(error?.shouldSuggestCamera),
    };
  }
};

export const buildInitialMediaState = (draft) => {
  const originalFile = draft?.originalFile || draft?.originalSelectedFile || null;
  const snapshotFile = draft?.snapshotFile || null;
  const selectionId = Number.isFinite(draft?.selectionId) ? draft.selectionId : 0;
  const snapshotStatus =
    draft?.snapshotStatus ||
    (snapshotFile ? "success" : originalFile ? "failed" : "idle");
  const selectedFile =
    snapshotFile ||
    originalFile ||
    draft?.uploadFile ||
    draft?.selectedFile ||
    draft?.file ||
    null;
  const mediaType =
    draft?.mediaType ||
    draft?.type ||
    (selectedFile ? inferSelectedMediaType(selectedFile, "image") : null);
  const hasSafeImageSnapshot =
    mediaType === "image" && snapshotFile && snapshotStatus === "success";
  const draftPreviewUrl = draft?.previewUrl || draft?.url || "";
  const shouldRebuildVideoPreview =
    mediaType === "video" &&
    (!draftPreviewUrl ||
      (typeof draftPreviewUrl === "string" && draftPreviewUrl.startsWith("blob:")));
  const previewUrl = shouldRebuildVideoPreview ? "" : draftPreviewUrl;
  const previewSource = shouldRebuildVideoPreview
    ? ""
    : draft?.previewSource || "";

  if (mediaType === "image" && !hasSafeImageSnapshot && selectedFile) {
    console.warn("[CATCH_IMG_DEBUG]", {
      stage: "invalid_image_upload_source_blocked",
      selectionId,
      snapshotStatus,
      reason: "draft_image_without_snapshot",
      hasSnapshotFile: Boolean(snapshotFile),
      hasOriginalFile: Boolean(originalFile),
    });
    return buildRejectedImageSelectionState(
      selectionId,
      draft?.nonBlockingMessage || CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE,
      snapshotStatus
    );
  }

  const uploadFile =
    draft?.uploadFile ||
    (snapshotFile && snapshotStatus === "success" ? snapshotFile : null);

  return {
    selectionId,
    selectedFile,
    originalFile,
    snapshotFile,
    snapshotStatus,
    uploadFile,
    uploadFilename: draft?.uploadFilename || "",
    uploadMimeType: draft?.uploadMimeType || "",
    mediaType,
    previewUrl,
    previewSource,
    previewStatus: shouldRebuildVideoPreview
      ? "idle"
      : draft?.previewStatus || (previewUrl ? "ready" : "idle"),
    exifStatus: draft?.exifStatus || "idle",
    submitReady: computeSubmitReady(uploadFile, draft?.blockingError || ""),
    blockingError: draft?.blockingError || "",
    blockingErrorCode: draft?.blockingErrorCode || "",
    nonBlockingMessage: draft?.nonBlockingMessage || "",
  };
};

export const buildMultipartMediaPart = (snapshot, asset) => {
  const file = asset?.snapshotFile || asset?.originalFile || null;
  const snapshotStatus = asset?.snapshotStatus || "idle";
  const sourceKind = asset?.snapshotFile
    ? "snapshot_file"
    : asset?.originalFile
      ? "original_file"
      : "missing";
  const mediaType =
    snapshot?.mediaType || inferSelectedMediaType(file, "image");
  if (!file) {
    console.warn("[CATCH_IMG_DEBUG]", {
      stage: "invariant_missing_upload_source",
      selectionId: snapshot?.selectionId ?? asset?.selectionId ?? null,
      snapshotStatus,
      sourceKind,
      hasSnapshotFile: Boolean(asset?.snapshotFile),
      hasOriginalFile: Boolean(asset?.originalFile),
    });
    throw buildCatchMediaClientError(
      CATCH_MULTIPART_BUILD_ERROR_MESSAGE,
      CATCH_MULTIPART_BUILD_ERROR_CODE
    );
  }

  if (mediaType === "image" && !asset?.snapshotFile) {
    console.warn("[CATCH_IMG_DEBUG]", {
      stage: "invalid_image_upload_source_blocked",
      selectionId: snapshot?.selectionId ?? asset?.selectionId ?? null,
      snapshotStatus,
      sourceKind,
      reason: "multipart_without_snapshot",
      hasSnapshotFile: Boolean(asset?.snapshotFile),
      hasOriginalFile: Boolean(asset?.originalFile),
    });
    throw buildCatchMediaClientError(
      CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE,
      CATCH_ANDROID_PICKER_UNREADABLE_ERROR_CODE
    );
  }

  const normalizedMimeType = normalizeMimeType(
    snapshot?.uploadMimeType || file?.type
  );
  const filename =
    snapshot?.uploadFilename ||
    buildUploadFilename({ file }, mediaType, normalizedMimeType);
  const finalMimeType =
    normalizeMimeType(file?.type) ||
    normalizedMimeType ||
    normalizeMimeType(mediaType === "video" ? "video/mp4" : "image/jpeg");

  const normalizedBlob = normalizeBlobMimeType(file, finalMimeType);
  if (normalizedBlob !== file) {
    console.warn("[CATCH_IMG_DEBUG]", {
      stage: "invariant_non_snapshot_blob_for_upload",
      selectionId: snapshot?.selectionId ?? asset?.selectionId ?? null,
      snapshotStatus,
    });
  }

  return {
    blob: file,
    filename,
  };
};
